import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable, RESET } from 'jotai/utils';
import { ICompanyDataModel } from '../../data-models/company.data-model';
import { getForesightStore } from '../../util/jotai-store';
import { fetchCompanyRaw } from '../queries/MaggieCompanyQueries';

type CompanyAtomValue = ICompanyDataModel | Promise<ICompanyDataModel | null> | null;

/**
 * Internal: per-company atom whose default fetches the company by id.
 * Reading this atom triggers the fetch (Suspense / async). Do not read directly
 * from places that should *peek* without fetching — use {@link getCompanySyncIfResolved}.
 */
const companyDataAtoms = atomFamily((companyId: number) =>
  atomWithDefault<CompanyAtomValue>(() => {
    if (companyId >= 0) {
      return fetchCompanyRaw(companyId);
    } else {
      return null;
    }
  })
);

/**
 * Synchronous mirror of resolved company values, populated only when
 * {@link companyStateJ} is explicitly written. Read this from derived atoms /
 * peek paths that must not trigger {@link fetchCompanyRaw}.
 *
 * Reactive: updates whenever any company is written via {@link companyStateJ}
 * or {@link batchSetCompaniesAtom}.
 */
export const companySyncCacheAtom = atom<Map<number, ICompanyDataModel>>(new Map());

function isPromiseLike(value: unknown): value is Promise<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'then' in value &&
    typeof (value as Promise<unknown>).then === 'function'
  );
}

/**
 * Public writable atom for a single company.
 *
 * Reads delegate to {@link companyDataAtoms}, preserving the auto-fetching /
 * Suspense behaviour that consumers rely on.
 *
 * Writes update both the underlying data atom and {@link companySyncCacheAtom},
 * so subsequent {@link getCompanySyncIfResolved} calls can return the value
 * without triggering a fetch.
 *
 * NOTE: When using the functional setter form (`set(companyStateJ(id), prev => ...)`)
 * for an id that has never been read or written, resolving `prev` will materialise
 * the underlying data atom and trigger `fetchCompanyRaw`. Existing functional-setter
 * call sites (`useReportingStatusAutoSave`, `useListActions`) operate on already-loaded
 * companies, so this is safe in practice — but new callers should ensure the company
 * is already in the cache before applying functional updates.
 *
 * For batch writes that span many companies, prefer {@link batchSetCompaniesAtom}
 * to avoid O(N) intermediate `Map` copies of the sync mirror.
 */
export const companyStateJ = atomFamily((companyId: number) =>
  atom(
    (get) => get(companyDataAtoms(companyId)),
    (get, set, value: CompanyAtomValue | ((prev: CompanyAtomValue) => CompanyAtomValue)) => {
      const next =
        typeof value === 'function'
          ? (value as (prev: CompanyAtomValue) => CompanyAtomValue)(get(companyDataAtoms(companyId)))
          : value;

      set(companyDataAtoms(companyId), next);

      // Only mirror fully-resolved, non-null company objects into the sync cache.
      // Promises and null values are not mirrored; null clears any previous entry.
      set(companySyncCacheAtom, (prev) => {
        const map = new Map(prev);
        if (next != null && !isPromiseLike(next)) {
          map.set(companyId, next as ICompanyDataModel);
        } else if (next === null) {
          map.delete(companyId);
        }
        return map;
      });
    }
  )
);

/**
 * Write-only batch setter for many companies at once.
 *
 * Avoids the O(N) `Map` copies that result from calling
 * `set(companyStateJ(id), company)` in a loop — each individual write triggers
 * a fresh copy of the sync-mirror Map. This atom updates each per-id data atom
 * (separate atoms in the family — cannot be batched at the atom level) and then
 * updates {@link companySyncCacheAtom} **once** with all entries.
 *
 * Use for cold-load population paths (`useInitializeMetricsData`, portfolio
 * fetch hooks, etc.). For one-off writes, `set(companyStateJ(id), value)`
 * remains the right call.
 */
export const batchSetCompaniesAtom = atom(null, (_get, set, companies: ReadonlyArray<ICompanyDataModel>) => {
  if (companies.length === 0) {
    return;
  }

  for (const company of companies) {
    set(companyDataAtoms(company.id), company);
  }

  set(companySyncCacheAtom, (prev) => {
    const map = new Map(prev);
    for (const company of companies) {
      map.set(company.id, company);
    }
    return map;
  });
});

/**
 * Write-only batch remover, the inverse of {@link batchSetCompaniesAtom}: each per-id
 * atom returns to its default (a fetch on next read for real ids, null for negative
 * ones) and the sync mirror forgets them in one copy. For seeded sample data that must
 * not outlive the screen that seeded it.
 */
export const batchRemoveCompaniesAtom = atom(null, (_get, set, companyIds: ReadonlyArray<number>) => {
  if (companyIds.length === 0) {
    return;
  }

  for (const companyId of companyIds) {
    set(companyDataAtoms(companyId), RESET);
  }

  set(companySyncCacheAtom, (prev) => {
    const map = new Map(prev);
    for (const companyId of companyIds) {
      map.delete(companyId);
    }
    return map;
  });
});

export const companyStateLoadable = atomFamily((companyId: number) => loadable(companyStateJ(companyId)));

/**
 * Synchronous read of a previously-saved company value.
 * Returns null when the company has not been explicitly saved (does not trigger a fetch).
 *
 * Use this from value getters, exporters, and other render-time code paths that must
 * not initiate network requests.
 */
export function getCompanySyncIfResolved(companyId: number): ICompanyDataModel | null {
  return getForesightStore().get(companySyncCacheAtom).get(companyId) ?? null;
}
